import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import * as api from '../../services/api';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  padding: 24px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  padding: 48px 40px;
  text-align: center;
`;

const Icon = styled.div`
  font-size: 52px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 12px;
`;

const Body = styled.p`
  font-size: 15px;
  color: #374151;
  line-height: 1.6;
  margin: 0 0 8px;
`;

const EmailHighlight = styled.span`
  font-weight: 600;
  color: #2563eb;
`;

const Note = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 16px 0 0;
  line-height: 1.5;
`;

const LoginLink = styled.a`
  display: inline-block;
  margin-top: 28px;
  padding: 11px 28px;
  background: #2563eb;
  color: white;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  &:hover { background: #1d4ed8; }
`;

const ErrorBox = styled.div`
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 6px;
  padding: 14px 16px;
  color: #dc2626;
  font-size: 14px;
  margin-top: 16px;
  text-align: left;
`;

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id') || '';

  const [state, setState] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      setError('Missing payment session. If you completed payment, check your email for a verification link.');
      return;
    }

    const verify = () => {
      attemptsRef.current += 1;
      api.verifyPaymentSession(sessionId)
        .then((res: any) => {
          if (res?.paid) {
            if (pollRef.current) clearInterval(pollRef.current);
            setEmail(res.email || '');
            setState('success');
          } else if (attemptsRef.current >= 10) {
            if (pollRef.current) clearInterval(pollRef.current);
            setState('error');
            setError('Payment verification timed out. If you were charged, please contact support.');
          }
        })
        .catch(() => {
          if (attemptsRef.current >= 10) {
            if (pollRef.current) clearInterval(pollRef.current);
            setState('error');
            setError('Could not verify your payment. Please contact support if you were charged.');
          }
        });
    };

    verify();
    pollRef.current = setInterval(verify, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [sessionId]);

  return (
    <Wrapper>
      <Card>
        {state === 'verifying' && (
          <>
            <Icon>⏳</Icon>
            <Title>Confirming your payment...</Title>
            <Body>Verifying with Stripe. This takes just a moment.</Body>
          </>
        )}

        {state === 'success' && (
          <>
            <Icon>✅</Icon>
            <Title>Payment confirmed!</Title>
            <Body>
              A verification email has been sent to{' '}
              <EmailHighlight>{email}</EmailHighlight>.
            </Body>
            <Body>Click the link in your email to activate your account and log in.</Body>
            <Note>
              Didn't receive it? Check your spam folder. The link expires in 24 hours.
            </Note>
            <LoginLink href="/login">Go to Login</LoginLink>
          </>
        )}

        {state === 'error' && (
          <>
            <Icon>❌</Icon>
            <Title>Something went wrong</Title>
            <ErrorBox>{error}</ErrorBox>
            <Note>
              Need help? Email us at <strong>support@drschmoe.ai</strong>
            </Note>
            <LoginLink href="/signup">Back to Sign Up</LoginLink>
          </>
        )}
      </Card>
    </Wrapper>
  );
};

export default PaymentSuccess;
